export const ACTIVE_QUEST_ROUTES_SESSION_STORAGE_KEY = 'activeQuestRoutes';
export const ACTIVE_QUEST_ROUTES_UPDATED_EVENT = 'activeQuestRoutesUpdated';
const GUEST_ACTIVE_QUEST_ROUTES_KEY = '__guest__';

export type ActiveQuestRoute = {
  id: string;
  lastActiveAt: string;
};

type ActiveQuestRoutesStorage = Record<string, ActiveQuestRoute[]>;

const getActiveQuestRoutesStorageKey = (userId?: string) => userId || GUEST_ACTIVE_QUEST_ROUTES_KEY;

const normalizeActiveQuestRoutes = (parsed: unknown): ActiveQuestRoute[] => {
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed
    .map((entry) => {
      if (typeof entry === 'string') {
        return { id: entry, lastActiveAt: '' };
      }

      if (entry && typeof entry === 'object' && 'id' in entry && entry.id) {
        return {
          id: String(entry.id),
          lastActiveAt: 'lastActiveAt' in entry && typeof entry.lastActiveAt === 'string' ? entry.lastActiveAt : '',
        };
      }

      return null;
    })
    .filter(Boolean) as ActiveQuestRoute[];
};

const getAllActiveQuestRoutes = (): ActiveQuestRoutesStorage => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(ACTIVE_QUEST_ROUTES_SESSION_STORAGE_KEY) || '[]');

    if (Array.isArray(parsed)) {
      return {
        [GUEST_ACTIVE_QUEST_ROUTES_KEY]: normalizeActiveQuestRoutes(parsed),
      };
    }

    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed).map(([key, routes]) => [key, normalizeActiveQuestRoutes(routes)])
    );
  } catch {
    return {};
  }
};

export const getActiveQuestRoutes = (userId?: string): ActiveQuestRoute[] => {
  return getAllActiveQuestRoutes()[getActiveQuestRoutesStorageKey(userId)] || [];
};

export const getActiveQuestRouteIds = (userId?: string): string[] => {
  return getActiveQuestRoutes(userId).map((route) => route.id);
};

export const getActiveQuestRouteLastActiveAt = (routeId: string, userId?: string): string => {
  return getActiveQuestRoutes(userId).find((route) => route.id === routeId)?.lastActiveAt || '';
};

const persistActiveQuestRoutes = (routesByUser: ActiveQuestRoutesStorage) => {
  try {
    sessionStorage.setItem(ACTIVE_QUEST_ROUTES_SESSION_STORAGE_KEY, JSON.stringify(routesByUser));
    window.dispatchEvent(new Event(ACTIVE_QUEST_ROUTES_UPDATED_EVENT));
  } catch {
    // Ignore unavailable storage.
  }
};

export const setActiveQuestRoute = (routeId: string, userId?: string) => {
  const activeRoutesByUser = getAllActiveQuestRoutes();
  const storageKey = getActiveQuestRoutesStorageKey(userId);
  const activeRoutes = activeRoutesByUser[storageKey] || [];
  const now = new Date().toISOString();
  const nextActiveRoutes = activeRoutes.some((route) => route.id === routeId)
    ? activeRoutes.map((route) => route.id === routeId ? { ...route, lastActiveAt: now } : route)
    : [...activeRoutes, { id: routeId, lastActiveAt: now }];

  persistActiveQuestRoutes({
    ...activeRoutesByUser,
    [storageKey]: nextActiveRoutes,
  });
};

export const removeActiveQuestRoute = (routeId: string, userId?: string) => {
  const activeRoutesByUser = getAllActiveQuestRoutes();
  const storageKey = getActiveQuestRoutesStorageKey(userId);
  const nextActiveRoutes = (activeRoutesByUser[storageKey] || []).filter((route) => route.id !== routeId);

  persistActiveQuestRoutes({
    ...activeRoutesByUser,
    [storageKey]: nextActiveRoutes,
  });
};
